import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/fsm';
const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const BusinessHours = () => {
  const { lang, t } = useLanguage();
  const f = t('fsm');
  const isRTL = lang === 'ar';

  const dayLabels = {
    sunday: f.day_sun || 'Sunday', monday: f.day_mon || 'Monday',
    tuesday: f.day_tue || 'Tuesday', wednesday: f.day_wed || 'Wednesday',
    thursday: f.day_thu || 'Thursday', friday: f.day_fri || 'Friday',
    saturday: f.day_sat || 'Saturday'
  };

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '', name_ar: '', timezone: 'Africa/Cairo',
    sunday: '09:00-17:00', monday: '09:00-17:00', tuesday: '09:00-17:00',
    wednesday: '09:00-17:00', thursday: '09:00-17:00', friday: '', saturday: '',
    is_default: 0
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const inputSt = {
    width: '100%', padding: '12px', border: '2px solid rgba(255,255,255,0.2)',
    borderRadius: '8px', backgroundColor: '#1a2038', color: '#f9fafb', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box'
  };

  const labelSt = { display: 'block', marginBottom: '8px', color: '#d1d5db', fontSize: '13px', fontWeight: '500' };

  const sectionSt = {
    backgroundColor: 'rgba(26, 32, 56, 0.8)', padding: '25px', borderRadius: '12px',
    marginBottom: '20px', border: '1px solid rgba(255, 255, 255, 0.1)'
  };

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/business-hours`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); if (d.success) setItems(d.data || []); }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const openNew = () => {
    setEditItem(null);
    setFormData({
      name: '', name_ar: '', timezone: 'Africa/Cairo',
      sunday: '09:00-17:00', monday: '09:00-17:00', tuesday: '09:00-17:00',
      wednesday: '09:00-17:00', thursday: '09:00-17:00', friday: '', saturday: '',
      is_default: 0
    });
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditItem(item);
    setFormData({
      name: item.name || '', name_ar: item.name_ar || '', timezone: item.timezone || 'Africa/Cairo',
      sunday: item.sunday || '', monday: item.monday || '', tuesday: item.tuesday || '',
      wednesday: item.wednesday || '', thursday: item.thursday || '',
      friday: item.friday || '', saturday: item.saturday || '',
      is_default: item.is_default || 0
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = editItem ? `${API}/business-hours/${editItem.id}` : `${API}/business-hours`;
      const method = editItem ? 'PUT' : 'POST';
      const body = { ...formData };
      DAYS.forEach(d => { if (!body[d]) body[d] = null; });
      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) { setShowModal(false); fetchData(); }
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(lang === 'ar' ? 'هل تريد الحذف؟' : 'Delete this schedule?')) return;
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API}/business-hours/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchData();
    } catch (err) { console.error(err); }
  };

  return (
    <Layout>
    <div style={{ direction: isRTL ? 'rtl' : 'ltr', padding: '30px', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '25px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: '700', color: '#f9fafb' }}>{f.business_hours_title || 'Business Hours'}</h1>
          <p style={{ margin: '4px 0 0', color: '#9ca3af', fontSize: '14px' }}>{f.business_hours_subtitle || 'Define weekly schedules for SLA clock calculation'}</p>
        </div>
        <button onClick={openNew} style={{
          padding: '10px 20px', borderRadius: '8px', border: 'none',
          backgroundColor: '#007bff', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '14px'
        }}>+ {f.new_schedule || 'New Schedule'}</button>
      </div>

      {loading ? (
        <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>{f.loading || 'Loading...'}</div>
      ) : items.length === 0 ? (
        <div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>🕐</div>
          <p>{f.no_schedules || 'No schedules yet'}</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '16px' }}>
          {items.map(item => (
            <div key={item.id} style={{ ...sectionSt, position: 'relative', marginBottom: 0 }}>
              {item.is_default ? (
                <span style={{ position: 'absolute', top: 12, left: isRTL ? 'auto' : 12, right: isRTL ? 12 : 'auto',
                  backgroundColor: 'rgba(66,133,244,0.2)', color: '#4285F4', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600'
                }}>{f.default_label || 'Default'}</span>
              ) : null}
              <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: '600', color: '#f9fafb' }}>
                {lang === 'ar' ? (item.name_ar || item.name) : item.name}
              </h3>
              <p style={{ margin: '0 0 12px', fontSize: '12px', color: '#6b7280' }}>🌍 {item.timezone}</p>
              <div style={{ fontSize: '13px' }}>
                {DAYS.map(d => (
                  <div key={d} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', opacity: item[d] ? 1 : 0.4 }}>
                    <span style={{ color: '#d1d5db' }}>{dayLabels[d]}</span>
                    <span style={{ fontWeight: '500', color: item[d] ? '#f9fafb' : '#6b7280' }}>{item[d] || (f.day_off || 'Off')}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button onClick={() => openEdit(item)} style={{
                  flex: 1, padding: '8px 12px', borderRadius: '8px', border: '2px solid rgba(255,255,255,0.15)',
                  backgroundColor: 'transparent', color: '#d1d5db', cursor: 'pointer', fontSize: '13px'
                }}>✏️ {f.edit || 'Edit'}</button>
                {!item.is_default && (
                  <button onClick={() => handleDelete(item.id)} style={{
                    padding: '8px 12px', borderRadius: '8px', border: '2px solid rgba(255,255,255,0.15)',
                    backgroundColor: 'transparent', color: '#ff6b6b', cursor: 'pointer', fontSize: '13px'
                  }}>🗑️</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }} onClick={() => setShowModal(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            backgroundColor: '#151b30', borderRadius: '16px', padding: '25px', width: '90%', maxWidth: '500px',
            maxHeight: '80vh', overflow: 'auto', direction: isRTL ? 'rtl' : 'ltr',
            border: '1px solid rgba(255,255,255,0.1)'
          }}>
            <h3 style={{ margin: '0 0 20px', fontSize: '18px', fontWeight: '600', color: '#f9fafb' }}>
              {editItem ? (f.edit_schedule || 'Edit Schedule') : (f.new_schedule || 'New Schedule')}
            </h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <label style={labelSt}>{f.name_en || 'Name (EN)'} *</label>
                <input type="text" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} style={inputSt} />
              </div>
              <div>
                <label style={labelSt}>{f.name_ar_label || 'Name (AR)'}</label>
                <input type="text" value={formData.name_ar} onChange={(e) => handleChange('name_ar', e.target.value)} style={{ ...inputSt, direction: 'rtl' }} />
              </div>
              <div>
                <label style={labelSt}>{f.timezone || 'Timezone'}</label>
                <input type="text" value={formData.timezone} onChange={(e) => handleChange('timezone', e.target.value)} style={inputSt} />
              </div>
              <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '12px' }}>
                <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#9ca3af' }}>
                  {f.bh_format || 'Format: HH:MM-HH:MM (leave empty for day off)'}
                </p>
                {DAYS.map(d => (
                  <div key={d} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <span style={{ width: '90px', fontSize: '13px', fontWeight: '500', color: '#d1d5db' }}>{dayLabels[d]}</span>
                    <input type="text" value={formData[d]}
                      onChange={(e) => handleChange(d, e.target.value)}
                      placeholder={f.day_off || 'Off'}
                      style={{ ...inputSt, flex: 1, width: 'auto' }} />
                  </div>
                ))}
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#d1d5db', fontSize: '14px' }}>
                <input type="checkbox" checked={!!formData.is_default}
                  onChange={(e) => handleChange('is_default', e.target.checked ? 1 : 0)}
                  style={{ width: 18, height: 18 }} />
                {f.set_as_default || 'Set as default'}
              </label>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button onClick={() => setShowModal(false)} style={{
                padding: '10px 20px', borderRadius: '8px', border: '2px solid rgba(255,255,255,0.2)',
                backgroundColor: 'transparent', cursor: 'pointer', color: '#d1d5db', fontSize: '14px'
              }}>{f.cancel || 'Cancel'}</button>
              <button onClick={handleSave} disabled={saving} style={{
                padding: '10px 20px', borderRadius: '8px', border: 'none',
                backgroundColor: saving ? '#6c757d' : '#007bff', color: '#fff',
                fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '14px'
              }}>{saving ? (f.saving || 'Saving...') : (f.save || 'Save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
    </Layout>
  );
};

export default BusinessHours;